import { useMemo, useState, useRef, useEffect } from 'react';
import allCS from '../../../../assets/cl.png';
import defaultFootball from '../../../../assets/escudo-default-foot.png';
import defaultOtherSport from '../../../../assets/escudo-default-general.png';
import './leagueFilter.sass';

/**
 * Componente de filtrado por liga
 * Muestra un dropdown personalizado con las ligas disponibles del deporte seleccionado
 * Solo se muestra si hay 2 o más ligas
 * 
 * @param {Object} props
 * @param {Array} props.tabsData - Datos de todas las tabs
 * @param {number} props.currentTab - Tab actualmente activa
 * @param {string} props.selectedSport - Código del deporte seleccionado
 * @param {string} props.selectedLeague - Liga seleccionada (null = todas)
 * @param {Function} props.onLeagueChange - Callback cuando cambia la liga
 */
export default function LeagueFilter({ tabsData, currentTab, selectedSport, selectedLeague, onLeagueChange, isViewingToday,selectedDate }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Obtener imagen por defecto según el deporte
  const getDefaultImage = (sportCode) => {
    return sportCode === 'FOOT' ? defaultFootball : defaultOtherSport;
  };

  // Obtener ligas disponibles para el deporte seleccionado
  const leagues = useMemo(() => {
    const currentTabData = tabsData[currentTab - 1];

    if (!currentTabData?.data?.sports || !selectedSport) {
      return [];
    }

    // Encontrar el deporte seleccionado
    const sport = currentTabData.data.sports.find(s => s.sport_code === selectedSport);

    if (!sport || !sport.leagues || sport.leagues.length < 2) {
      return [];
    }

    // Retornar las ligas con su imagen
    return sport.leagues.map(league => ({
      id: league.name,
      name: league.name,
      order: league.order,
      image: league.image || null
    }));
  }, [currentTab, selectedSport, tabsData[currentTab - 1]?.date, tabsData[currentTab - 1]?.data?.sports], selectedDate);

  // Obtener el nombre de la liga seleccionada
  const selectedLeagueName = useMemo(() => {
    if (!selectedLeague) return 'TODOS';
    return leagues.find(l => l.id === selectedLeague)?.name || 'TODOS';
  }, [selectedLeague, leagues]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Si no hay suficientes ligas, no renderizar
  if (!leagues || leagues.length < 2) {
    return null;
  }

  // cerrar al seleccionar una liga
  const handleLeagueSelect = (leagueId) => {
    onLeagueChange(leagueId === 'all' ? null : leagueId);
    setIsOpen(false);
  };

  return (
    <div className="league-filter-container" ref={dropdownRef}>
      <button
        className="league-filter-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Filtrar por liga"
        aria-expanded={isOpen}
      >
        <span className="league-filter-text">{(selectedLeagueName).toUpperCase()}</span>
        <span className="league-filter-arrow">▼</span>
      </button>

      {isOpen && (
        <div className="league-filter-dropdown">
          <div
            className="league-filter-option"
            onClick={() => handleLeagueSelect('all')}
          >
            <div className="league-option-icon">
              <img
                src={allCS}
                alt={"todas las ligas"}
                className="league-icon-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
            </div>
            <span className="league-option-name">TODOS</span>
          </div>
          {leagues.map(league => (
            <div
              key={league.id}
              className={`league-filter-option ${selectedLeague === league.id ? 'active' : ''}`}
              onClick={() => handleLeagueSelect(league.id)}
            >
              <div className="league-option-icon">
                <img
                  src={league.image || getDefaultImage(selectedSport)}
                  alt={league.name}
                  className="league-icon-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <div
                  className="league-icon-placeholder"
                  style={{ display: 'none' }}
                />
              </div>
              <span className="league-option-name">{(league.name).toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}